import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Check, X, Zap, Star, Rocket, HelpCircle, Loader2 } from 'lucide-react';
import { authService, publicService } from '../services';
import type { Plan } from '../types';
import { settingsService, type PublicSettings } from '../services/settings.service';

export default function Plans() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [faqLoading, setFaqLoading] = useState(true);
  const [publicSettings, setPublicSettings] = useState<PublicSettings>({});

  useEffect(() => {
    loadPlans();
    (async () => {
      try {
        setFaqLoading(true);
        const s = await settingsService.getPublicSettings();

        // Processa faq_items se vier no formato aninhado (com categories)
        if (s?.faq_items && Array.isArray(s.faq_items)) {
          // Verifica se tem a estrutura aninhada com "questions"
          const firstItem = s.faq_items[0];
          if (firstItem && 'questions' in firstItem && Array.isArray(firstItem.questions)) {
            // Achata o array pegando todas as questions de todas as categorias
            const flatQuestions = s.faq_items.flatMap((category: any) =>
              category.questions || []
            );
            s.faq_items = flatQuestions;
          }
        }

        setPublicSettings(s || {});
      } finally {
        setFaqLoading(false);
      }
    })();
  }, []);

  const loadPlans = async () => {
    try {
      setLoading(true);
      const data = await publicService.getPlans().catch(() => []);
      setPlans(data);
    } catch (error) {
      console.error('Erro ao carregar planos:', error);
    } finally {
      setLoading(false);
    }
  };

  const isCompany = authService.isAuthenticated() && authService.isCompany();

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  // Ícones para os planos (baseado no índice ou nome)
  const getIconForPlan = (plan: Plan, index: number) => {
    const name = plan.name.toLowerCase();
    if (name.includes('premium') || name.includes('pro')) return Rocket;
    if (name.includes('destaque') || name.includes('plus')) return Star;
    return Zap;
  };

  // Verifica se é o plano mais popular (segundo plano ou o que tiver "destaque" no nome)
  const isPopular = (plan: Plan, index: number) => {
    const name = plan.name.toLowerCase();
    return name.includes('destaque') || index === 1;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Seo
        title="Planos e Preços - Anuncie sua vaga"
        description="Escolha o plano ideal para divulgar suas vagas em Brusque. Destaque sua vaga e encontre talentos com mais rapidez."
        canonical={window.location.origin + '/planos'}
        ogImage={window.location.origin + '/logo-vagas.svg'}
      />
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-br from-brand-400 to-brand-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-white mb-6">Anuncie sua Vaga</h1>
            <p className="text-xl text-brand-100 mb-8">
              Escolha o tipo de vaga ideal para encontrar os melhores profissionais em Brusque
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-brand-100">
              <Check className="size-4" />
              <span>Pague por vaga • Sem mensalidade • Suporte incluído</span>
            </div>
          </div>
        </div>
      </section>

      {/* Vagas */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="size-8 animate-spin text-brand-600 mx-auto mb-4" />
              <p className="text-gray-600">Carregando tipos de vagas...</p>
            </div>
          ) : plans.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Nenhum tipo de vaga disponível no momento.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {plans.filter(p => p.is_active).map((plan, index) => {
                const Icon = getIconForPlan(plan, index);
                const popular = isPopular(plan, index);
                return (
                  <Card
                    key={plan.id}
                    className={`relative overflow-hidden ${
                      popular
                        ? 'border-brand-300 shadow-xl scale-105'
                        : 'border-gray-200'
                    }`}
                  >
                    {popular && (
                      <div className="absolute top-0 right-0 bg-brand-300 text-brand-900 px-4 py-1 text-sm font-medium">
                        Mais Popular
                      </div>
                    )}

                    <div className="p-8">
                      {/* Ícone e Nome */}
                      <div className="flex items-center gap-3 mb-4">
                        <div
                          className={`${
                            popular ? 'bg-brand-300' : 'bg-gray-200'
                          } p-3 rounded-lg`}
                        >
                          <Icon
                            className={`size-6 ${
                              popular ? 'text-white' : 'text-gray-600'
                            }`}
                          />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900">
                            {plan.name}
                          </h3>
                        </div>
                      </div>

                      {/* Descrição */}
                      <p className="text-gray-600 mb-6">{plan.description}</p>

                      {/* Preço */}
                      <div className="mb-6">
                        {plan.discount_percent > 0 && (
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-lg text-gray-400 line-through">
                              {formatCurrency(plan.price)}
                            </span>
                            <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
                              {plan.discount_percent}% OFF
                            </span>
                          </div>
                        )}
                        <div className="flex items-baseline gap-2">
                          <span className="text-4xl font-bold text-gray-900">
                            {formatCurrency(plan.price * (1 - (plan.discount_percent || 0) / 100))}
                          </span>
                          <span className="text-gray-600">/{plan.duration_days} dias</span>
                        </div>
                      </div>

                      {/* Botão */}
                      <Link to={isCompany ? "/empresa/vaga/nova" : "/login"} className="block mb-6">
                        <Button
                          className={`w-full ${
                            popular
                              ? 'bg-brand-300 hover:bg-brand-500 text-white'
                              : ''
                          }`}
                          variant={popular ? 'default' : 'outline'}
                          size="lg"
                        >
                          {isCompany ? 'Anunciar vaga' : 'Entrar para anunciar'}
                        </Button>
                      </Link>

                      {/* Features */}
                      {plan.features && plan.features.length > 0 && (
                        <div className="space-y-3">
                          {plan.features.map((raw, idx) => {
                            const line = raw?.trim?.() ?? '';
                            if (!line) return null;
                            const isIncluded = /^(✅|✔|[Vv]\b)/.test(line);
                            const isExcluded = /^(❌|✖|[Xx]\b)/.test(line);
                            const text = (isIncluded || isExcluded)
                              ? line.replace(/^(?:✅|✔|❌|✖|[VvXx])\s*[:\-]?\s*/, '')
                              : line;
                            if (!isIncluded && !isExcluded) {
                              return (
                                <div key={idx} className="pt-2">
                                  <span className="text-gray-900 text-sm font-medium">{text}</span>
                                </div>
                              );
                            }
                            return (
                              <div key={idx} className="flex items-start gap-3">
                                {isIncluded ? (
                                  <Check className="size-5 text-brand-600 flex-shrink-0 mt-0.5" />
                                ) : (
                                  <X className="size-5 text-red-600 flex-shrink-0 mt-0.5" />
                                )}
                                <span className={`text-sm ${isExcluded ? 'text-gray-600' : 'text-gray-700'}`}>{text}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-center text-gray-900 mb-12">
              Perguntas Frequentes
            </h2>

            {faqLoading ? (
              <div className="text-center py-8 text-gray-600">Carregando...</div>
            ) : (Array.isArray(publicSettings.faq_items) && publicSettings.faq_items.length > 0) ? (
              <div className="space-y-6">
                {publicSettings.faq_items.map((item, idx) => (
                  <Card className="p-6" key={idx}>
                    <div className="flex items-start gap-4">
                      <HelpCircle className="size-6 text-brand-600 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">
                          {item.question}
                        </h3>
                        <p className="text-gray-700 whitespace-pre-wrap">
                          {item.answer}
                        </p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-600">
                Em breve adicionaremos perguntas frequentes.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 bg-gradient-to-br from-brand-400 to-brand-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-white mb-4">Pronto para contratar?</h2>
            <p className="text-xl text-brand-100 mb-8">
              Publique sua vaga hoje e encontre os melhores talentos de Brusque
            </p>
            <Link to="/empresa/vaga/nova">
              <Button size="lg" className="bg-white text-brand-700 hover:bg-gray-100">
                Anunciar minha vaga
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
